(ns hypnos.translator.consult
  "Translators between Clojure and GraphQL for the consult model."
  (:require [clojure.spec.alpha :as s]
            [clojure.string :as str]
            [hypnos.time :as t]
            [hypnos.translator.catalog :as t-c]
            [hypnos.translator.organization :as t-o]
            [medley.core :as mc]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Specs

;; consult-state
(def ^{:const true :private true} consult-states
  #{:consult-state/new
    :consult-state/in-progress
    :consult-state/pending
    :consult-state/completed
    :consult-state/canceled
    :consult-state/expired})
(s/def ::consult-state consult-states)
(s/def ::consult-state-gql (set (map name consult-states)))

;; communication-channel
(def ^{:const true :private true} communication-channels
  #{:communication-channel/email
    :communication-channel/sms})
(s/def ::communication-channel communication-channels)
(s/def ::communication-channel-gql (set (map name communication-channels)))

;; gender
(def ^{:const true :private true} genders
  #{:gender/female
    :gender/male
    :gender/unknown})
(s/def ::gender genders)
(s/def ::gender-gql (set (map name genders)))

;; modality
(def ^{:const true :private true} modalities
  #{:modality/cpap
    :modality/autopap-smartpap
    :modality/bipap-vpap
    :modality/other
    :modality/unknown})
(s/def ::modality modalities)
(s/def ::modality-gql (set (map name modalities)))

;; sleep-position
(def ^{:const true :private true} sleep-positions
  #{:sleep-position/side
    :sleep-position/back
    :sleep-position/stomach
    :sleep-position/upright
    :sleep-position/unknown})
(s/def ::sleep-position sleep-positions)
(s/def ::sleep-position-gql (set (map name sleep-positions)))

;; issue
(def ^{:const true :private true} issues
  #{:issue/claustrophobic
    :issue/breathing
    :issue/eye
    :issue/nose
    :issue/forehead
    :issue/face})
(s/def ::issue issues)
(s/def ::issue-gql (set (map name issues)))

;; service type
(def ^{:const true :private true} service-types
  #{:service-type/new-setup
    :service-type/exchange
    :service-type/refit
    :service-type/repap})
(s/def ::service-type service-types)
(s/def ::service-type-gql (set (map name service-types)))

;; selector-type
(def ^{:const true :private true} selector-types
  #{:selector-type/prescriber
    :selector-type/therapist
    :selector-type/unknown})
(s/def ::selector-type selector-types)
(s/def ::selector-type-gql (set (map name selector-types)))

;; timezone
(def ^{:const true :private true} timezones
  #{:timezone/est
    :timezone/est5edt
    :timezone/cst6cdt
    :timezone/mst
    :timezone/mst7mdt
    :timezone/pst
    :timezone/pst8pdt
    :timezone/akdt
    :timezone/hst})
(s/def ::timezone timezones)
(s/def ::timezone-gql (set (map name timezones)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- changed?
  [{:keys [value _dirty? _touched?]}]
  (not (nil? value)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Enums

(defn consult-state->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "consult-state/" (str/replace s #"_" "-")))))

(s/fdef consult-state->clj
  :args (s/cat :s (s/or :state ::consult-state-gql :nil nil?))
  :ret (s/or :state ::consult-state :nil nil?))

(defn consult-state->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef consult-state->gql
  :args (s/cat :o (s/or :state ::consult-state :nil nil?))
  :ret (s/or :state ::consult-state-gql :nil nil?))

(defn communication-channel->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "communication-channel/" s))))

(s/fdef communication-channel->clj
  :args (s/cat :s (s/or :channel ::communication-channel-gql :nil nil?))
  :ret (s/or :channel ::communication-channel :nil nil?))

(defn communication-channel->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef communication-channel->gql
  :args (s/cat :o (s/or :channel ::communication-channel :nil nil?))
  :ret (s/or :channel ::communication-channel-gql :nil nil?))

(defn gender->clj
  [g]
  (when-not (str/blank? g)
    (keyword (str "gender/" g))))

(s/fdef gender->clj
  :args (s/cat :s (s/or :gender ::gender-gql :nil nil?))
  :ret (s/or :gender ::gender :nil nil?))

(defn gender->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef gender->gql
  :args (s/cat :o (s/or :gender ::gender :nil nil?))
  :ret (s/or :gender ::gender-gql :nil nil?))

(defn modality->clj
  [m]
  (when-not (str/blank? m)
    (keyword (str "modality/" (str/replace m #"_" "-")))))

(s/fdef modality->clj
  :args (s/cat :s (s/or :modality ::modality-gql :nil nil?))
  :ret (s/or :modality ::modality :nil nil?))

(defn modality->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef modality->gql
  :args (s/cat :o (s/or :modality ::modality :nil nil?))
  :ret (s/or :modality ::modality-gql :nil nil?))

(defn issue->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "issue/" s))))

(s/fdef issue->clj
  :args (s/cat :s (s/or :issue ::issue-gql :nil nil?))
  :ret (s/or :issue ::issue :nil nil?))

(defn issue->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef issue->gql
  :args (s/cat :o (s/or :issue ::issue :nil nil?))
  :ret (s/or :issue ::issue-gql :nil nil?))

(defn selector-type->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "selector-type/" (str/replace s #"_" "-")))))

(s/fdef selector-type->clj
  :args (s/cat :s (s/or :type ::selector-type-gql :nil nil?))
  :ret (s/or :type ::selector-type :nil nil?))

(defn selector-type->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef selector-type->gql
  :args (s/cat :o (s/or :type ::selector-type :nil nil?))
  :ret (s/or :type ::selector-type-gql :nil nil?))

(defn service-type->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "service-type/" (str/replace s #"_" "-")))))

(s/fdef service-type->clj
  :args (s/cat :s (s/or :type ::service-type-gql :nil nil?))
  :ret (s/or :type ::service-type :nil nil?))

(defn service-type->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef service-type->gql
  :args (s/cat :o (s/or :type ::service-type :nil nil?))
  :ret (s/or :type ::service-type-gql :nil nil?))

(defn sleep-position->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "sleep-position/" s))))

(s/fdef sleep-position->clj
  :args (s/cat :s (s/or :position ::sleep-position-gql :nil nil?))
  :ret (s/or :position ::sleep-position :nil nil?))

(defn sleep-position->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef sleep-position->gql
  :args (s/cat :o (s/or :position ::sleep-position :nil nil?))
  :ret (s/or :position ::sleep-position-gql :nil nil?))

(defn timezone->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "timezone/" s))))

(s/fdef timezone->clj
  :args (s/cat :s (s/or :timezone ::timezone-gql :nil nil?))
  :ret (s/or :timezone ::timezone :nil nil?))

(defn timezone->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef timezone->gql
  :args (s/cat :o (s/or :timezone ::timezone :nil nil?))
  :ret (s/or :timezone ::timezone-gql :nil nil?))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Entities

(declare anatomy->clj anatomy->gql consult->clj consult->gql recommendation->clj recommendation->gql)

;; Patient

(defn patient-consult->clj
  "Convert the patient-consult to the Clojure format."
  [m]
  (cond-> {}
    (:id m)                           (assoc :consult/id (mc/uuid (:id m)))
    (:key m)                          (assoc :consult/key (:key m))
    (:service_type m)                 (assoc :consult/service-type (service-type->clj (:service_type m)))
    (some? (:mask_was_comfortable m)) (assoc :consult/mask-was-comfortable? (:mask_was_comfortable m))
    (some? (:has_portrait m))         (assoc :consult/has-portrait? (:has_portrait m))
    (:status m)                       (assoc :consult/status (consult-state->clj (:status m)))))

(defn patient-consult->gql
  "Convert the patient-consult to the GraphQL format."
  [m]
  (cond-> {}
    (:consult/id m)                            (assoc :id (str (:consult/id m)))
    (:consult/key m)                           (assoc :key (:consult/key m))
    (:consult/service-type m)                  (assoc :service_type (service-type->gql (:consult/service-type m)))
    (some? (:consult/mask-was-comfortable? m)) (assoc :mask_was_comfortable (:consult/mask-was-comfortable? m))
    (some? (:consult/has-portrait? m))         (assoc :has_portrait (:consult/has-portrait? m))
    (:consult/status m)                        (assoc :status (consult-state->gql (:consult/status m)))))

;; Consult

(defn consult->clj
  "Convert the consult to the Clojure format."
  [m]
  (cond-> {}
    (:id m)                           (assoc :consult/id (mc/uuid (:id m)))
    (:key m)                          (assoc :consult/key (:key m))
    (:consented_at m)                 (assoc :consult/consented-at (t/from-aws-date-time (:consented_at m)))
    (:service_type m)                 (assoc :consult/service-type (service-type->clj (:service_type m)))
    (:weight m)                       (assoc :consult/weight (:weight m))
    (:ahi m)                          (assoc :consult/ahi (float (:ahi m)))
    (:modality m)                     (assoc :consult/modality (modality->clj (:modality m)))
    (:cpap m)                         (assoc :consult/cpap (:cpap m))
    (:ipap m)                         (assoc :consult/ipap (:ipap m))
    (:epap m)                         (assoc :consult/epap (:epap m))
    (:manufacturer m)                 (assoc :consult/manufacturer (t-c/manufacturer->clj (:manufacturer m)))
    (:make m)                         (assoc :consult/make (t-c/make->clj (:make m)))
    (:model m)                        (assoc :consult/model (t-c/model->clj (:model m)))
    (some? (:mask_was_comfortable m)) (assoc :consult/mask-was-comfortable? (:mask_was_comfortable m))
    (some? (:take_dentures_out m))    (assoc :consult/take-dentures-out? (:take_dentures_out m))
    true                              (assoc :consult/sleep-positions (set (mapv sleep-position->clj (:sleep_positions m))))
    (:mouth_breathing_level m)        (assoc :consult/mouth-breathing-level (:mouth_breathing_level m))
    (some? (:eye_dryness  m))         (assoc :consult/eye-dryness? (:eye_dryness m))
    (:claustrophobia_level m)         (assoc :consult/claustrophobia-level (:claustrophobia_level m))
    true                              (assoc :consult/issues (set (mapv issue->clj (:issues m))))
    (some? (:deviated_septum m))      (assoc :consult/deviated-septum? (:deviated_septum m))
    (some? (:chronic_issues m))       (assoc :consult/chronic-issues? (:chronic_issues m))
    (some? (:pacemaker m))            (assoc :consult/pacemaker? (:pacemaker m))
    (some? (:sleep_motion m))         (assoc :consult/sleep-motion? (:sleep_motion m))
    (:recommendations m)              (assoc :consult/recommendations (mapv recommendation->clj (:recommendations m)))
    (:recommendations_note m)         (assoc :consult/recommendations-note (:recommendations_note m))
    (:mask m)                         (assoc :consult/mask (t-c/model->clj (:mask m)))
    (:sized_mask m)                   (assoc :consult/sized-mask (t-c/model->clj (:sized_mask m)))
    (:selector_type m)                (assoc :consult/selector-type (selector-type->clj (:selector_type m)))
    (:selector m)                     (assoc :consult/selector (t-o/user->clj (:selector m)))
    (:code m)                         (assoc :consult/code (:code m))
    (:status m)                       (assoc :consult/status (consult-state->clj (:status m)))
    (:recorded_at m)                  (assoc :consult/recorded-at (t/from-aws-date-time (:recorded_at m)))
    (:user m)                         (assoc :consult/user (t-o/user->clj (:user m)))))

(defn consult->gql
  "Convert the consult to the GraphQL format."
  [m]
  (cond-> {}
    (:consult/id m)                            (assoc :id (str (:consult/id m)))
    (:consult/key m)                           (assoc :key (:consult/key m))
    (:consult/consented-at m)                  (assoc :consented_at (t/to-aws-date-time (:consult/consented-at m)))
    (:consult/service-type m)                  (assoc :service_type (service-type->gql (:consult/service-type m)))
    (:consult/weight m)                        (assoc :weight (:consult/weight m))
    (:consult/ahi m)                           (assoc :ahi (float (:consult/ahi m)))
    (:consult/modality m)                      (assoc :modality (modality->gql (:consult/modality m)))
    (:consult/cpap m)                          (assoc :cpap (:consult/cpap m))
    (:consult/ipap m)                          (assoc :ipap (:consult/ipap m))
    (:consult/epap m)                          (assoc :epap (:consult/epap m))
    (:consult/manufacturer m)                  (assoc :manufacturer (t-c/manufacturer->gql (:consult/manufacturer m)))
    (:consult/make m)                          (assoc :make (t-c/make->gql (:consult/make m)))
    (:consult/model m)                         (assoc :model (t-c/model->gql (:consult/model m)))
    (some? (:consult/mask-was-comfortable? m)) (assoc :mask_was_comfortable (:consult/mask-was-comfortable? m))
    (some? (:consult/take-dentures-out? m))    (assoc :take_dentures_out (:consult/take-dentures-out? m))
    true                                       (assoc :sleep_positions (mapv sleep-position->gql (:consult/sleep-positions m)))
    (:consult/mouth-breathing-level m)         (assoc :mouth_breathing_level (:consult/mouth-breathing-level m))
    (some? (:consult/eye-dryness?  m))         (assoc :eye_dryness (:consult/eye-dryness? m))
    (:consult/claustrophobia-level m)          (assoc :claustrophobia_level (:consult/claustrophobia-level m))
    true                                       (assoc :issues (mapv issue->gql (:consult/issues m)))
    (some? (:consult/deviated-septum? m))      (assoc :deviated_septum (:consult/deviated-septum? m))
    (some? (:consult/chronic-issues? m))       (assoc :chronic_issues (:consult/chronic-issues? m))
    (some? (:consult/pacemaker? m))            (assoc :pacemaker (:consult/pacemaker? m))
    (some? (:consult/sleep-motion? m))         (assoc :sleep_motion (:consult/sleep-motion? m))
    (:consult/recommendations m)               (assoc :recommendations (mapv recommendation->gql (:consult/recommendations m)))
    (:consult/recommendations-note m)          (assoc :recommendations_note (:consult/recommendations-note m))
    (:consult/mask m)                          (assoc :mask (t-c/model->gql (:consult/mask m)))
    (:consult/sized-mask m)                    (assoc :sized_mask (t-c/model->gql (:consult/sized-mask m)))
    (:consult/selector-type m)                 (assoc :selector_type (selector-type->gql (:consult/selector-type m)))
    (:consult/selector m)                      (assoc :selector (t-o/user->gql (:consult/selector m)))
    (:consult/code m)                          (assoc :code (:consult/code m))
    (:consult/status m)                        (assoc :status (consult-state->gql (:consult/status m)))
    (:consult/recorded-at m)                   (assoc :recorded_at (t/to-aws-date-time (:consult/recorded-at m)))
    (:consult/user m)                          (assoc :user (t-o/user->gql (:consult/user m)))))

(defn recommendation->clj
  "Convert the recommendation to the Clojure format."
  [m]
  (cond-> {}
    (:id m)             (assoc :recommendation/id (mc/uuid (:id m)))
    ;; TODO this is a hack right now ... should really contain the full model
    (:model m)          (assoc :recommendation/model (mc/uuid (get-in m [:model :id])))
    (:confidence_min m) (assoc :recommendation/confidence-min (float (:confidence_min m)))
    (:confidence_max m) (assoc :recommendation/confidence-max (float (:confidence_max m)))
    (:selector_type m)  (assoc :recommendation/selector-type (selector-type->clj (:selector_type m)))))

(defn recommendation->gql
  "Convert the recommendation to the GraphQL format."
  [m]
  (when (:recommendation/model m)
    (cond-> {}
      (:recommendation/id m)             (assoc :id (str (:recommendation/id m)))
      ;; TODO this is a hack right now ... should really contain the full model
      (:recommendation/model m)          (assoc :model {:id (str (:recommendation/model m))})
      (:recommendation/confidence-min m) (assoc :confidence_min (:recommendation/confidence-min m))
      (:recommendation/confidence-max m) (assoc :confidence_max (:recommendation/confidence-max m))
      (:recommendation/selector-type m)  (assoc :selector_type (selector-type->gql (:recommendation/selector-type m))))))

;; State <-> DB

(defn consult->db
  "Convert the consult to a db format."
  [state]
  (cond-> {}
    ;; patient/image
    (changed? (get-in state [:consult :patient/image]))
    (assoc :patient/image
           (get-in state [:consult :patient/image :value]))
    ;; consult/id
    (get-in state [:consult :consult/id :value])
    (assoc :consult/id
           (get-in state [:consult :consult/id :value]))
    ;; consult/user
    (get-in state [:consult :consult/user :value])
    (assoc-in [:consult/user :user/id]
           (get-in state [:consult :consult/user :value]))
    ;; consult/consented-at
    (get-in state [:consult :consult/consented-at])
    (assoc :consult/consented-at
           (get-in state [:consult :consult/consented-at :value]))
    ;; consult/service-type
    (get-in state [:consult :consult/service-type])
    (assoc :consult/service-type
           (get-in state [:consult :consult/service-type :value]))
    ;; consult/manufacturer
    (changed? (get-in state [:consult :consult/manufacturer]))
    (assoc :consult/manufacturer
           {:manufacturer/id (mc/uuid (get-in state [:consult :consult/manufacturer :value]))})
    ;; consult/make
    (changed? (get-in state [:consult :consult/make]))
    (assoc :consult/make
           {:make/id (mc/uuid (get-in state [:consult :consult/make :value]))})
    ;; consult/model
    (changed? (get-in state [:consult :consult/model]))
    (assoc :consult/model
           {:model/id (mc/uuid (get-in state [:consult :consult/model :value]))})
    ;; consult/mask-was-comfortable?
    (changed? (get-in state [:consult :consult/mask-was-comfortable?]))
    (assoc :consult/mask-was-comfortable?
           (get-in state [:consult :consult/mask-was-comfortable? :value]))
    ;; consult/ahi
    (changed? (get-in state [:consult :consult/ahi]))
    (assoc :consult/ahi
           (get-in state [:consult :consult/ahi :value]))
    ;; consult/modality
    (changed? (get-in state [:consult :consult/modality]))
    (assoc :consult/modality
           (get-in state [:consult :consult/modality :value]))
    ;; consult/cpap
    (changed? (get-in state [:consult :consult/cpap]))
    (assoc :consult/cpap
           (get-in state [:consult :consult/cpap :value]))
    ;; consult/ipap
    (changed? (get-in state [:consult :consult/ipap]))
    (assoc :consult/ipap
           (get-in state [:consult :consult/ipap :value]))
    ;; consult/epap
    (changed? (get-in state [:consult :consult/epap]))
    (assoc :consult/epap-pressure
           (get-in state [:consult :consult/epap :value]))
    ;; consult/weight
    (changed? (get-in state [:consult :consult/weight]))
    (assoc :consult/weight
           (get-in state [:consult :consult/weight :value]))
    ;; consult/take-dentures-out?
    (changed? (get-in state [:consult :consult/take-dentures-out?]))
    (assoc :consult/take-dentures-out?
           (get-in state [:consult :consult/take-dentures-out? :value]))
    ;; consult/sleep-positions
    (changed? (get-in state [:consult :consult/sleep-positions]))
    (assoc :consult/sleep-positions
           (get-in state [:consult :consult/sleep-positions :value]))
    ;; consult/mouth-breathing-level
    (changed? (get-in state [:consult :consult/mouth-breathing-level]))
    (assoc :consult/mouth-breathing-level
           (get-in state [:consult :consult/mouth-breathing-level :value]))
    ;; consult/eye-dryness?
    (changed? (get-in state [:consult :consult/eye-dryness?]))
    (assoc :consult/eye-dryness?
           (get-in state [:consult :consult/eye-dryness? :value]))
    ;; consult/claustrophobia-level
    (changed? (get-in state [:consult :consult/claustrophobia-level]))
    (assoc :consult/claustrophobia-level
           (get-in state [:consult :consult/claustrophobia-level :value]))
    ;; consult/issues
    (changed? (get-in state [:consult :consult/issues]))
    (assoc :consult/issues
           (get-in state [:consult :consult/issues :value]))
    ;; consult/chronic-issues?
    (changed? (get-in state [:consult :consult/chronic-issues?]))
    (assoc :consult/chronic-issues?
           (get-in state [:consult :consult/chronic-issues? :value]))
    ;; consult/pacemaker?
    (changed? (get-in state [:consult :consult/pacemaker?]))
    (assoc :consult/pacemaker?
           (get-in state [:consult :consult/pacemaker? :value]))
    ;; consult/sleep-motion?
    (changed? (get-in state [:consult :consult/sleep-motion?]))
    (assoc :consult/sleep-motion?
           (get-in state [:consult :consult/sleep-motion? :value]))
    ;; consult/deviated-septum?
    (changed? (get-in state [:consult :consult/deviated-septum?]))
    (assoc :consult/deviated-septum?
           (get-in state [:consult :consult/deviated-septum? :value]))))

(defn consult->state
  "Convert the (current) consult to state format."
  [consult]
  (cond-> {}
    (get consult :patient/image)
    (assoc-in [:consult :patient/image]
              {:value    (get consult :patient/image)
               :dirty?   false
               :touched? false})
    (get consult :consult/id)
    (assoc-in [:consult :consult/id]
              {:value    (:consult/id consult)
               :dirty?   false
               :touched? false})
    (get consult :consult/user)
    (assoc-in [:consult :consult/user]
              {:value    (get-in consult [:consult/user :user/id])
               :dirty?   false
               :touched? false})
    (get consult :consult/consented-at)
    (assoc-in [:consult :consult/consented-at]
              {:value    (:consult/consented-at consult)
               :dirty?   false
               :touched? false})
    (get consult :consult/service-type)
    (assoc-in [:consult :consult/service-type]
              {:value    (get consult :consult/service-type)
               :dirty?   false
               :touched? false})
    (get consult :consult/manufacturer)
    (assoc-in [:consult :consult/manufacturer]
              {:value    (str (get-in consult [:consult/manufacturer :manufacturer/id]))
               :dirty?   false
               :touched? false})
    (get consult :consult/make)
    (assoc-in [:consult :consult/make]
              {:value    (str (get-in consult [:consult/make :make/id]))
               :dirty?   false
               :touched? false})
    (get consult :consult/model)
    (assoc-in [:consult :consult/model]
              {:value    (str (get-in consult [:consult/model :model/id]))
               :dirty?   false
               :touched? false})
    (some? (get consult :consult/mask-was-comfortable?))
    (assoc-in [:consult :consult/mask-was-comfortable?]
              {:value    (get consult :consult/mask-was-comfortable?)
               :dirty?   false
               :touched? false})
    (get consult :consult/ahi)
    (assoc-in [:consult :consult/ahi]
              {:value    (get consult :consult/ahi)
               :dirty?   false
               :touched? false})
    (get consult :consult/modality)
    (assoc-in [:consult :consult/modality]
              {:value    (get consult :consult/modality)
               :dirty?   false
               :touched? false})
    (get consult :consult/cpap)
    (assoc-in [:consult :consult/cpap]
              {:value    (get consult :consult/cpap)
               :dirty?   false
               :touched? false})
    (get consult :consult/ipap)
    (assoc-in [:consult :consult/ipap]
              {:value    (get consult :consult/ipap)
               :dirty?   false
               :touched? false})
    (get consult :consult/epap)
    (assoc-in [:consult :consult/epap]
              {:value    (get consult :consult/epap)
               :dirty?   false
               :touched? false})
    ;; patient submitted data
    (get consult :consult/weight)
    (assoc-in [:consult :consult/weight]
              {:value    (get consult :consult/weight)
               :dirty?   false
               :touched? false})
    (some? (get consult :consult/take-dentures-out?))
    (assoc-in [:consult :consult/take-dentures-out?]
              {:value    (get consult :consult/take-dentures-out?)
               :dirty?   false
               :touched? false})
    (get consult :consult/sleep-positions)
    (assoc-in [:consult :consult/sleep-positions]
              {:value    (get consult :consult/sleep-positions)
               :dirty?   false
               :touched? false})
    (get consult :consult/mouth-breathing-level)
    (assoc-in [:consult :consult/mouth-breathing-level]
              {:value    (get consult :consult/mouth-breathing-level)
               :dirty?   false
               :touched? false})
    (some? (get consult :consult/eye-dryness?))
    (assoc-in [:consult :consult/eye-dryness?]
              {:value    (get consult :consult/eye-dryness?)
               :dirty?   false
               :touched? false})
    (get consult :consult/claustrophobia-level)
    (assoc-in [:consult :consult/claustrophobia-level]
              {:value    (get consult :consult/claustrophobia-level)
               :dirty?   false
               :touched? false})
    (get consult :consult/issues)
    (assoc-in [:consult :consult/issues]
              {:value    (get consult :consult/issues)
               :dirty?   false
               :touched? false})
    (some? (get consult :consult/chronic-issues?))
    (assoc-in [:consult :consult/chronic-issues?]
              {:value    (get consult :consult/chronic-issues?)
               :dirty?   false
               :touched? false})
    (some? (get consult :consult/pacemaker?))
    (assoc-in [:consult :consult/pacemaker?]
              {:value    (get consult :consult/pacemaker?)
               :dirty?   false
               :touched? false})
    (some? (get consult :consult/sleep-motion?))
    (assoc-in [:consult :consult/sleep-motion?]
              {:value    (get consult :consult/sleep-motion?)
               :dirty?   false
               :touched? false})
    (some? (get consult :consult/deviated-septum?))
    (assoc-in [:consult :consult/deviated-septum?]
              {:value    (get consult :consult/deviated-septum?)
               :dirty?   false
               :touched? false})))
